import { DiscountSegment } from '@/types/transaction';

export interface DisplayDiscountSplit {
  /**
   * Subtotal to show = sum of the DISCOUNTED line totals (list subtotal minus the
   * line-level discount portion). Product/sale discounts are already reflected in
   * each line item's price, so folding them into the subtotal keeps the line rows
   * and the subtotal in agreement.
   */
  displaySubtotalCents: number;
  /** Cart-level discount total (a code / discount-on-total) — the only discount NOT already in the line prices. */
  cartLevelDiscountCents: number;
  /** The cart-level segments, for the per-source breakdown under the Discount row. */
  cartLevelSegments: DiscountSegment[];
}

/**
 * EWM-716 — split `calculateTransaction`'s discount for DISPLAY so a product/sale
 * discount is only ever shown ONCE — on the line item, via its discounted price —
 * and never repeated as a separate summary "Discount" row (which double-counts it
 * against the discounted line total).
 *
 * Only **cart-level** discounts (`targetsTotal` — a coupon code / discount-on-total)
 * are NOT baked into the line prices, so only those form the summary Discount row.
 * The displayed subtotal is therefore the sum of the discounted line totals:
 * `list subtotal − line-level discount`.
 *
 * The raw `calculateTransaction.subtotal`/`discount` are unchanged — callers still
 * use the pre-discount subtotal for the FIS free-freight threshold. All amounts are
 * centAmounts (the unit `calculateTransaction` segments carry).
 */
export const splitDiscountForDisplay = (
  subtotalCents: number,
  discountCents: number,
  segments: DiscountSegment[] = [],
): DisplayDiscountSplit => {
  const cartLevelSegments = segments.filter((segment) => segment.targetsTotal && segment.discountedAmount > 0);
  const cartLevelDiscountCents = cartLevelSegments.reduce((sum, segment) => sum + segment.discountedAmount, 0);
  const lineLevelDiscountCents = discountCents - cartLevelDiscountCents;
  return {
    displaySubtotalCents: subtotalCents - lineLevelDiscountCents,
    cartLevelDiscountCents,
    cartLevelSegments,
  };
};
